import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { getPendingReports } from "../../../store/rbm/actions";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SearchIcon from "@mui/icons-material/Search";
import ReactPaginate from "react-paginate";
import defaultProfile from "../../assets/default-profile.jpg";
import moment from "moment";
import PendingReportEvaluator from "../components/PendingReportEvaluator";

const QUARTERS = {
  1: "Quarter 1 (Jul–Sep)",
  2: "Quarter 2 (Oct–Dec)",
  3: "Quarter 3 (Jan–Mar)",
  4: "Quarter 4 (Apr–Jun)",
};

const ITEMS_PER_PAGE = 10;

const PendingReports = ({
  selectedFiscalYear,
  selectedPlanningEntityId,
  getPendingReports,
}) => {
  const [pendingReports, setPendingReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);

  const [showEvaluator, setShowEvaluator] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);

  const fetchReports = () => {
    setLoading(true);
    getPendingReports({}, (status, data) => {
      setLoading(status);
      if (!status && Array.isArray(data)) {
        setPendingReports(data);
      }
    });
  };

  useEffect(() => {
    fetchReports();
  }, [selectedFiscalYear, selectedPlanningEntityId]);

  // Filtered list
  const filteredReports = useMemo(() => {
    return pendingReports.filter((r) => {
      const matchSearch =
        r.employeeNames?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchQuarter =
        selectedQuarter === "all" || +r.quarterId === +selectedQuarter;
      return matchSearch && matchQuarter;
    });
  }, [pendingReports, searchTerm, selectedQuarter]);

  // Pagination
  const pageCount = Math.ceil(filteredReports.length / ITEMS_PER_PAGE);
  const paginatedReports = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE;
    return filteredReports.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredReports, currentPage]);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box>
      {/* Filters Row */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        mb={2}
      >
        {/* Search */}
        <TextField
          size="small"
          placeholder="Search by employee or activity"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />

        {/* Quarter Filter */}
        <TextField
          select
          size="small"
          label="Filter by Quarter"
          value={selectedQuarter}
          onChange={(e) => {
            setSelectedQuarter(e.target.value);
            setCurrentPage(0);
          }}
          sx={{ width: { xs: "100%", sm: 200 } }}
        >
          <MenuItem value="all">All Quarters</MenuItem>
          {Object.entries(QUARTERS).map(([q, label]) => (
            <MenuItem key={q} value={q}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        {/* Refresh */}
        <Button
          startIcon={<RefreshIcon />}
          variant="outlined"
          onClick={fetchReports}
          sx={{ whiteSpace: "nowrap" }}
        >
          Refresh
        </Button>
      </Stack>

      {/* Loading */}
      {loading && (
        <>
          <Skeleton variant="rectangular" height={90} className="mb-2" />
          <Skeleton variant="rectangular" height={90} className="mb-2" />
          <Skeleton variant="rectangular" height={90} className="mb-2" />
        </>
      )}

      {/* Pending reports list */}
      {!loading && paginatedReports.length > 0 && (
        <>
          <Stack spacing={2}>
            {paginatedReports.map((report, i) => {
              return (
                <Card
                  key={report.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "#fafafa",
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    {/* LEFT SIDE */}
                    <Box sx={{ flex: 1, pr: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                          src={defaultProfile}
                          alt={report.employeeNames}
                          sx={{ width: 44, height: 44 }}
                        />
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {report.employeeNames}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {report.unitName} • {report.positionName}
                          </Typography>
                        </Box>
                      </Stack>

                      <Divider sx={{ my: 1 }} />

                      <Typography
                        variant="subtitle2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <strong>
                          {i + 1 + currentPage * ITEMS_PER_PAGE}.{" "}
                          {report.itemTypeName}:
                        </strong>
                        &nbsp;{report.name}
                      </Typography>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        mt={1}
                        flexWrap="wrap"
                      >
                        <Chip
                          size="small"
                          color="primary"
                          label={`Achieved: ${report.achievedTarget} / ${report.targetShare} ${report.measurementUnit}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Weight: ${report.weightName}`}
                        />
                        <Chip
                          size="small"
                          color="info"
                          variant="outlined"
                          label={`Deadline: ${moment(report.endOn).format(
                            "ll"
                          )}`}
                        />
                        <Chip
                          size="small"
                          color="default"
                          variant="outlined"
                          label={
                            QUARTERS[report.quarterId] || "Unknown Quarter"
                          }
                        />
                        {report.isRRAKey && (
                          <Chip
                            size="small"
                            color="primary"
                            icon={<i className="fas fa-key" />}
                            label="Key Priority"
                          />
                        )}
                      </Stack>
                    </Box>

                    {/* RIGHT SIDE: Evaluate button */}
                    <Box>
                      <Tooltip title="Evaluate this report">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<AssignmentTurnedInIcon />}
                          onClick={() => {
                            setSelectedReportId(report.id);
                            setShowEvaluator(true);
                          }}
                        >
                          Evaluate
                        </Button>
                      </Tooltip>
                    </Box>
                  </Stack>
                </Card>
              );
            })}
          </Stack>

          {/* Pagination controls */}
          {filteredReports.length > ITEMS_PER_PAGE && (
            <Box
              mt={3}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ReactPaginate
                breakLabel="..."
                nextLabel={
                  <>
                    <span className="pr-1 d-none d-md-inline">Next</span>
                    <i className="fas fa-angle-double-right"></i>
                  </>
                }
                previousLabel={
                  <>
                    <i className="fas fa-angle-double-left"></i>
                    <span className="pl-1 d-none d-md-inline">Previous</span>
                  </>
                }
                pageRangeDisplayed={3}
                marginPagesDisplayed={1}
                onPageChange={handlePageClick}
                pageCount={pageCount}
                renderOnZeroPageCount={null}
                containerClassName="pagination"
                pageLinkClassName="btn btn-outline-info btn-sm mr-1 ml-1"
                previousLinkClassName="btn btn-outline-info btn-sm mr-1 ml-1"
                nextLinkClassName="btn btn-outline-info btn-sm mr-1 ml-1"
                activeLinkClassName="active"
                forcePage={currentPage}
              />
            </Box>
          )}
        </>
      )}

      {/* No reports */}
      {!loading && filteredReports.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            border: "1px dashed #ccc",
            borderRadius: 2,
            backgroundColor: "#fafafa",
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No pending reports found for your filters.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchReports}
          >
            Refresh
          </Button>
        </Box>
      )}

      {showEvaluator && (
        <PendingReportEvaluator
          open={showEvaluator}
          onClose={() => setShowEvaluator(false)}
          reports={pendingReports}
          setPendingReports={setPendingReports}
          selectedReportId={selectedReportId}
          // evaluateSubmission={evaluateSubmission} // pass your action
        />
      )}
    </Box>
  );
};

const mapStateToProps = ({ selectedFiscalYear, selectedPlanningEntityId }) => {
  return {
    selectedFiscalYear,
    selectedPlanningEntityId,
  };
};
export default connect(mapStateToProps, { getPendingReports })(PendingReports);
